import { useNavigate } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn, formatQuota } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useAccount } from "@/hooks/useAccount";
import type { BillingCycle, PlanDto } from "@/types/api";

interface PlanCardProps {
  plan: PlanDto;
  cycle: BillingCycle;
  highlighted?: boolean;
  onConsultRequested?: () => void;
}

/**
 * One pricing card. Two distinct behaviors:
 *
 *   Standard plans — Get Started → /api/checkout/quick → window.location
 *     to the HostShop basket-direct URL.
 *
 *   Custom plans (no twentyi_product_code) — Talk to Sales → opens the
 *     consultation modal via onConsultRequested().
 *
 * Highlighted state ("Most popular") gets the navy filled card + gradient
 * border — matches the Email Growth landing page treatment.
 */
export function PlanCard({ plan, cycle, highlighted, onConsultRequested }: PlanCardProps) {
  const navigate = useNavigate();

  // Admins manage the catalogue — they can't purchase. Only look up the
  // account when signed in, so anonymous visitors don't fire a 401 /me call.
  const { status } = useAuth();
  const { data: account } = useAccount({ enabled: status === "authenticated" });
  const isAdmin =
    account?.user_role === "Admin" || account?.user_role === "SuperAdmin";

  const isCustom = !plan.twentyi_product_code;
  const price = priceFor(plan, cycle);
  const cadenceLabel = cycleLabel(cycle);
  const monthlyEquivalent = monthlyEquivalentFor(plan, cycle);
  const monthsFree = monthsFreeFor(plan, cycle);

  const features = featuresFor(plan);

  function onGetStarted() {
    if (isAdmin) {
      navigate("/admin/plans");
      return;
    }
    if (isCustom) {
      onConsultRequested?.();
      return;
    }
    // In-app purchase flow: pick a domain → details → checkout. No more
    // bounce to the external HostShop storefront.
    navigate(`/buy/${plan.slug}?cycle=${cycle}`);
  }

  return (
    <Card
      className={cn(
        "group relative flex h-full flex-col p-6 transition-[transform,box-shadow] duration-200 sm:p-7",
        "hover:-translate-y-1",
        highlighted
          ? "border-primary/40 ring-1 ring-primary/15 elevate-lg"
          : "hover:elevate-lg"
      )}
    >
      {highlighted && (
        <>
          {/* soft brand glow behind the popular card */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-6 -top-6 -z-10 h-24 rounded-full bg-primary/15 blur-2xl"
          />
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-foreground shadow-md shadow-primary/30">
              <Sparkles className="h-3 w-3" />
              Most popular
            </span>
          </div>
        </>
      )}

      <div>
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {plan.plan_type}
        </p>
        <h3 className="mt-1.5 font-display text-2xl font-bold tracking-tight">{plan.name}</h3>
        {plan.description && (
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{plan.description}</p>
        )}
      </div>

      <div className="mt-5 flex items-baseline gap-1.5">
        {isCustom ? (
          <span className="font-display text-3xl font-bold tracking-tight">Custom</span>
        ) : (
          <>
            <span className="font-display text-[15px] font-semibold text-muted-foreground">$</span>
            <span className="font-display text-[44px] font-extrabold leading-none tracking-tight">
              {Math.round(price)}
            </span>
            <span className="text-sm text-muted-foreground">/{cadenceLabel}</span>
          </>
        )}
      </div>
      <p className="mt-1.5 h-4 text-xs text-muted-foreground">
        {!isCustom && monthlyEquivalent && cycle !== "monthly"
          ? `≈ $${monthlyEquivalent.toFixed(0)}/month${
              monthsFree > 0 ? ` · ${monthsFree} month${monthsFree === 1 ? "" : "s"} free` : ""
            }`
          : isCustom ? "Tailored to your portfolio" : ""}
      </p>

      <div className="my-6 h-px bg-border" />

      <ul className="space-y-3 text-sm">
        {features.map(f => (
          <li key={f} className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-brand-green/12 text-brand-green">
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            <span className="text-foreground/90">{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-7">
        <Button
          onClick={onGetStarted}
          className="w-full"
          size="lg"
          variant={highlighted ? "default" : isCustom ? "outline" : "default"}
        >
          {isAdmin ? "Manage plans" : isCustom ? "Talk to sales" : "Get started"}
        </Button>
      </div>
    </Card>
  );
}

// ── Helpers ──────────────────────────────────────────────────
function priceFor(plan: PlanDto, cycle: BillingCycle): number {
  if (cycle === "annually")  return plan.price_annually  ?? plan.price_monthly * 12;
  if (cycle === "quarterly") return plan.price_quarterly ?? plan.price_monthly * 3;
  return plan.price_monthly;
}

function monthlyEquivalentFor(plan: PlanDto, cycle: BillingCycle): number | null {
  if (cycle === "annually"  && plan.price_annually)  return plan.price_annually  / 12;
  if (cycle === "quarterly" && plan.price_quarterly) return plan.price_quarterly / 3;
  return null;
}

/** Whole months a customer skips paying on the annual price vs 12× monthly
 *  (e.g. $290/yr on a $29/mo plan → 2 months free). 0 = no badge. */
function monthsFreeFor(plan: PlanDto, cycle: BillingCycle): number {
  if (cycle !== "annually" || !plan.price_annually || plan.price_monthly <= 0) return 0;
  const free = Math.round(12 - plan.price_annually / plan.price_monthly);
  return free > 0 ? free : 0;
}

function cycleLabel(cycle: BillingCycle): string {
  return cycle === "annually" ? "yr" : cycle === "quarterly" ? "qtr" : "mo";
}

function featuresFor(plan: PlanDto): string[] {
  const out: string[] = [];

  if (plan.mailboxes_allowed > 0) {
    out.push(`${formatQuota(plan.mailboxes_allowed)} mailbox${plan.mailboxes_allowed === 1 ? "" : "es"}`);
  }
  if (plan.bulk_emails_allowed > 0) {
    out.push(`${plan.bulk_emails_allowed.toLocaleString()} bulk emails / month`);
  }
  if (plan.storage_gb_allowed > 0) {
    out.push(`${plan.storage_gb_allowed} GB storage`);
  }
  if (plan.websites_allowed > 0) {
    out.push(`${formatQuota(plan.websites_allowed)} website${plan.websites_allowed === 1 ? "" : "s"}`);
  }
  if (plan.domain_capacity > 1) {
    out.push(`Up to ${plan.domain_capacity} domains`);
  }
  if (plan.ssl_certs_allowed > 0) {
    out.push(`${formatQuota(plan.ssl_certs_allowed)} SSL certificate${plan.ssl_certs_allowed === 1 ? "" : "s"}`);
  }
  if (plan.aliases_allowed === -1) {
    out.push("Unlimited aliases & forwarders");
  } else if (plan.aliases_allowed > 0) {
    out.push(`${plan.aliases_allowed} aliases`);
  }

  return out;
}
